import { useEffect, useState } from "react";

const SRC = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_MAP_KEY}&libraries=places`;

const isReady = () => !!(window.google && window.google.maps && window.google.maps.places);

let pending = null;

/**
 * Loads the Google Maps JS API (+ places) on demand, once.
 * Resolves with `true` when ready, `false` if it failed / timed out.
 * Kept out of index.html so it doesn't block first paint.
 */
export const loadGoogleMaps = () => {
  if (isReady()) return Promise.resolve(true);
  if (pending) return pending;

  pending = new Promise((resolve) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      s.onerror = () => {
        pending = null;
        resolve(false);
      };
      document.head.appendChild(s);
    }
    const started = Date.now();
    const tick = () => {
      if (isReady()) return resolve(true);
      if (Date.now() - started > 15000) {
        pending = null;
        return resolve(false);
      }
      setTimeout(tick, 50);
      return undefined;
    };
    tick();
  });
  return pending;
};

/** Returns true once the Google Maps API is available (loads it on mount). */
export const useGoogleMaps = () => {
  const [ready, setReady] = useState(isReady());
  useEffect(() => {
    let alive = true;
    if (!ready) loadGoogleMaps().then((ok) => alive && setReady(ok));
    return () => {
      alive = false;
    };
  }, [ready]);
  return ready;
};
